import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { subscriptionService } from '../../services/subscriptionService';
import { marketplaceService } from '../../services/marketplaceService';

const steps = ['Select Products', 'Set Frequency', 'Choose Payment', 'Review & Create'];

const frequencyOptions = [
  {
    value: 'weekly',
    label: 'Weekly',
    description: 'Every 7 days',
    icon: '📅'
  },
  {
    value: 'biweekly',
    label: 'Bi-weekly',
    description: 'Every 14 days',
    icon: '📆'
  },
  {
    value: 'monthly',
    label: 'Monthly',
    description: 'Every 30 days',
    icon: '🗓️'
  },
  {
    value: 'quarterly',
    label: 'Quarterly',
    description: 'Every 90 days',
    icon: '📋'
  }
];

const CreateSubscription = ({ onSuccess, onCancel }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState({
    title: '',
    name: '',
    items: [],
    frequency: 'monthly',
    startDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    deliveryAddress: '',
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Bangladesh'
    }
  });
  
  // Product selection states
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // User data
  const [userPaymentMethods, setUserPaymentMethods] = useState([]);
  const [userAddresses, setUserAddresses] = useState([]);
  
  // Address dialog states
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Bangladesh'
  });
  
  // Payment method dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: 'card',
    paymentMethod: 'credit_card',
    last4: '',
    brand: 'Visa',
    upiId: '',
    walletProvider: '',
    label: ''
  });

  useEffect(() => {
    loadProducts();
    loadUserData();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      console.log('Loading products for subscription...');
      
      // Get all products from marketplace
      const response = await marketplaceService.getProducts();
      console.log('Full products response:', response);
      
      if (response && response.success) {
        // The backend returns data in response.data.products
        const allProducts = response.data?.products || response.products || [];
        console.log('All products found:', allProducts);
        
        // Filter for medicine products
        const medicineProducts = allProducts.filter(
          product => {
            const isMedicine = product.category === 'medicine' || 
                             product.category === 'Medicine' ||
                             (product.name && (
                               product.name.toLowerCase().includes('medicine') ||
                               product.name.toLowerCase().includes('tablet') ||
                               product.name.toLowerCase().includes('capsule') ||
                               product.name.toLowerCase().includes('syrup') ||
                               product.name.toLowerCase().includes('monas') ||
                               product.name.toLowerCase().includes('paracetamol')
                             ));
            console.log(`Product ${product.name}: category=${product.category}, isMedicine=${isMedicine}`);
            return isMedicine && (product.isActive !== false);
          }
        );
        
        setProducts(medicineProducts);
        console.log('Filtered medicine products:', medicineProducts);
        
        if (medicineProducts.length === 0) {
          console.log('No medicine products found. All products:', 
            allProducts.map(p => ({ name: p.name, category: p.category }))
          );
        }
      } else {
        console.error('Products response not successful:', response);
        setError(response?.message || 'Failed to load products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Failed to load products. Please check console for details.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserData = async () => {
    try {
      // Load user's saved payment methods and addresses
      // This would come from user profile or payment service
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        setUserAddresses([
          {
            id: 'address1',
            label: 'Home',
            address: user.address || 'No address saved'
          }
        ]);
        
        // In a real app, you'd load saved payment methods
        setUserPaymentMethods([
          {
            id: 'credit_card_default',
            type: 'card',
            paymentMethod: 'credit_card',
            last4: '1234',
            brand: 'Visa',
            label: 'Default Credit Card (****1234)'
          },
          {
            id: 'upi_default',
            type: 'upi',
            paymentMethod: 'upi',
            upiId: 'user@upi',
            label: 'Default UPI (user@upi)'
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAddressSelection = (event) => {
    const value = event.target.value;
    if (value === 'new') {
      setShowAddressDialog(true);
    } else {
      setSubscriptionData(prev => ({
        ...prev,
        deliveryAddress: value
      }));
    }
  };

  const handleAddNewAddress = () => {
    const addressString = `${newAddress.street}, ${newAddress.city}, ${newAddress.state} ${newAddress.zipCode}, ${newAddress.country}`;
    const newAddressObj = {
      id: `addr_${Date.now()}`,
      label: newAddress.label || 'New Address',
      address: addressString
    };
    
    setUserAddresses(prev => [...prev, newAddressObj]);
    setSubscriptionData(prev => ({
      ...prev,
      deliveryAddress: newAddressObj.id,
      shippingAddress: { ...newAddress }
    }));
    
    // Reset form and close dialog
    setNewAddress({
      label: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Bangladesh'
    });
    setShowAddressDialog(false);
  };

  const handleCloseAddressDialog = () => {
    setShowAddressDialog(false);
    setNewAddress({
      label: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Bangladesh'
    });
  };

  const handlePaymentMethodSelection = (event) => {
    const value = event.target.value;
    if (value === 'new') {
      setShowPaymentDialog(true);
    } else {
      setSubscriptionData(prev => ({
        ...prev,
        paymentMethod: value
      }));
    }
  };

  const handleAddNewPaymentMethod = () => {
    const newPaymentMethodObj = {
      id: `payment_${Date.now()}`,
      type: newPaymentMethod.type,
      paymentMethod: newPaymentMethod.paymentMethod,
      last4: newPaymentMethod.last4,
      brand: newPaymentMethod.brand,
      upiId: newPaymentMethod.upiId,
      walletProvider: newPaymentMethod.walletProvider,
      label: newPaymentMethod.label || `${newPaymentMethod.type.toUpperCase()} Payment`
    };
    
    setUserPaymentMethods(prev => [...prev, newPaymentMethodObj]);
    setSubscriptionData(prev => ({
      ...prev,
      paymentMethod: newPaymentMethodObj.id
    }));
    
    // Reset form and close dialog
    setNewPaymentMethod({
      type: 'card',
      paymentMethod: 'credit_card',
      last4: '',
      brand: 'Visa',
      upiId: '',
      walletProvider: '',
      label: ''
    });
    setShowPaymentDialog(false);
  };

  const handleClosePaymentDialog = () => {
    setShowPaymentDialog(false);
    setNewPaymentMethod({
      type: 'card',
      paymentMethod: 'credit_card',
      last4: '',
      brand: 'Visa',
      upiId: '',
      walletProvider: '',
      label: ''
    });
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0: // Products selection
        if (!subscriptionData.items || subscriptionData.items.length === 0) {
          setError('Please select at least one product');
          return false;
        }
        if (!subscriptionData.title || !subscriptionData.title.trim()) {
          setError('Please enter a subscription name');
          return false;
        }
        break;
      case 1: // Frequency
        if (!subscriptionData.frequency) {
          setError('Please select a delivery frequency');
          return false;
        }
        break;
      case 2: // Payment
        if (!subscriptionData.paymentMethod) {
          setError('Please select a payment method');
          return false;
        }
        if (!subscriptionData.deliveryAddress && !subscriptionData.shippingAddress.street) {
          setError('Please select a delivery address or add a new one');
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  const addProductToSubscription = (product) => {
    const existingItem = subscriptionData.items.find(item => item.productId === product._id);
    
    if (existingItem) {
      setSubscriptionData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }));
    } else {
      setSubscriptionData(prev => ({
        ...prev,
        items: [...prev.items, {
          productId: product._id,
          productName: product.name,
          price: product.price,
          quantity: 1,
          image: product.image
        }]
      }));
    }
  };

  const updateItemQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItemFromSubscription(productId);
      return;
    }
    
    setSubscriptionData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    }));
  };

  const removeItemFromSubscription = (productId) => {
    setSubscriptionData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.productId !== productId)
    }));
  };

  const calculateTotal = () => {
    return subscriptionData.items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  const handleCreateSubscription = async () => {
    try {
      setLoading(true);
      
      // Prepare the subscription data in the format expected by backend
      const selectedPaymentMethod = userPaymentMethods.find(pm => pm.id === subscriptionData.paymentMethod);
      
      const subscriptionPayload = {
        title: subscriptionData.title,
        description: subscriptionData.description || `Subscription for ${subscriptionData.title}`,
        items: subscriptionData.items,
        frequency: subscriptionData.frequency,
        startDate: subscriptionData.startDate,
        paymentMethod: selectedPaymentMethod?.paymentMethod || 'credit_card',
        savedPaymentInfo: selectedPaymentMethod ? {
          type: selectedPaymentMethod.type,
          last4: selectedPaymentMethod.last4,
          brand: selectedPaymentMethod.brand,
          upiId: selectedPaymentMethod.upiId,
          walletProvider: selectedPaymentMethod.walletProvider,
          isDefault: true
        } : {
          type: 'card',
          last4: '1234',
          brand: 'Visa',
          isDefault: true
        },
        shippingAddress: subscriptionData.shippingAddress,
        settings: {
          autoRenewal: true,
          notifications: true
        }
      };
      
      // If user selected an existing address, we need to get the address details
      if (subscriptionData.deliveryAddress && !subscriptionData.shippingAddress.street) {
        const selectedAddress = userAddresses.find(addr => addr.id === subscriptionData.deliveryAddress);
        if (selectedAddress) {
          // Parse the address string to extract components
          const addressParts = selectedAddress.address.split(', ');
          subscriptionPayload.shippingAddress = {
            street: addressParts[0] || '',
            city: addressParts[1] || '',
            state: addressParts[2]?.split(' ')[0] || '',
            zipCode: addressParts[2]?.split(' ')[1] || '',
            country: addressParts[3] || 'Bangladesh'
          };
        }
      }
      
      console.log('Sending subscription payload:', subscriptionPayload);
      
      const response = await subscriptionService.createSubscription(subscriptionPayload);
      
      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      setError('Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = (products || []).filter(product =>
    product && product.name && 
    (product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {/* Subscription Name */}
            <TextField
              fullWidth
              label="Subscription Name"
              placeholder="e.g., Monthly Medicine Pack"
              value={subscriptionData.title}
              onChange={(e) => setSubscriptionData(prev => ({ ...prev, title: e.target.value }))}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: isDarkMode ? '#64748B' : '#94A3B8',
                  opacity: 1
                }
              }}
            />

            {/* Product Search */}
            <TextField
              fullWidth
              label="Search Products"
              placeholder="Search for medicines..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ 
                  mr: 1, 
                  color: isDarkMode ? '#60A5FA' : '#64748B' 
                }} />
              }}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: isDarkMode ? '#64748B' : '#94A3B8',
                  opacity: 1
                }
              }}
            />

            {/* Selected Items */}
            {subscriptionData.items.length > 0 && (
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  mb: 3,
                  bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(240, 249, 255, 0.5)',
                  border: isDarkMode 
                    ? '1px solid rgba(59, 130, 246, 0.2)' 
                    : '1px solid rgba(30, 64, 175, 0.1)',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
                >
                  Selected Items ({subscriptionData.items.length})
                </Typography>
                <List>
                  {subscriptionData.items.map((item, index) => (
                    <React.Fragment key={item.productId}>
                      <ListItem>
                        <ListItemText
                          primary={item.productName}
                          secondary={`৳${item.price} each`}
                          primaryTypographyProps={{
                            sx: { color: isDarkMode ? '#E0F2FE' : '#1E293B' }
                          }}
                          secondaryTypographyProps={{
                            sx: { color: isDarkMode ? '#94A3B8' : '#64748B' }
                          }}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => updateItemQuantity(item.productId, item.quantity - 1)}
                              sx={{ color: isDarkMode ? '#60A5FA' : '#1E40AF' }}
                            >
                              <RemoveIcon />
                            </IconButton>
                            <Typography 
                              sx={{ 
                                minWidth: 30, 
                                textAlign: 'center',
                                color: isDarkMode ? '#E0F2FE' : '#1E293B'
                              }}
                            >
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => updateItemQuantity(item.productId, item.quantity + 1)}
                              sx={{ color: isDarkMode ? '#60A5FA' : '#1E40AF' }}
                            >
                              <AddIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeItemFromSubscription(item.productId)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < subscriptionData.items.length - 1 && (
                        <Divider 
                          sx={{ 
                            borderColor: isDarkMode 
                              ? 'rgba(59, 130, 246, 0.2)' 
                              : 'rgba(30, 64, 175, 0.1)'
                          }} 
                        />
                      )}
                    </React.Fragment>
                  ))}
                </List>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mt: 2, 
                    textAlign: 'right',
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                    fontWeight: 700
                  }}
                >
                  Total: ৳{calculateTotal().toFixed(2)}
                </Typography>
              </Paper>
            )}

            {/* Available Products */}
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
            >
              Available Products
            </Typography>
            
            {/* Loading State */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredProducts.length === 0 ? (
              /* Empty State */
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography 
                  variant="body1"
                  sx={{ color: isDarkMode ? '#BFDBFE' : '#64748B' }}
                >
                  No medicine products available. Please ensure products are added to the marketplace.
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ 
                    mt: 2,
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                    '&:hover': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                      bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                    }
                  }}
                  onClick={loadProducts}
                >
                  Refresh Products
                </Button>
              </Box>
            ) : (
              /* Products Grid */
              <Grid container spacing={2}>
                {filteredProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product._id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                        border: isDarkMode 
                          ? '1px solid rgba(59, 130, 246, 0.2)' 
                          : '1px solid rgba(30, 64, 175, 0.1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: isDarkMode
                            ? '0 12px 40px rgba(59, 130, 246, 0.2)'
                            : '0 12px 40px rgba(30, 64, 175, 0.15)',
                          borderColor: isDarkMode 
                            ? 'rgba(59, 130, 246, 0.4)' 
                            : 'rgba(30, 64, 175, 0.2)'
                        }
                      }}
                      onClick={() => addProductToSubscription(product)}
                    >
                      {product.image && (
                        <CardMedia
                          component="img"
                          height="140"
                          image={product.image}
                          alt={product.name}
                        />
                      )}
                      <CardContent>
                        <Typography 
                          variant="h6" 
                          component="div" 
                          noWrap
                          sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
                        >
                          {product.name}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 1,
                            color: isDarkMode ? '#94A3B8' : '#64748B'
                          }}
                        >
                          {product.description}
                        </Typography>
                        <Typography 
                          variant="h6"
                          sx={{ 
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                            fontWeight: 700
                          }}
                        >
                          ৳{product.price}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          sx={{ 
                            mt: 1,
                            bgcolor: isDarkMode
                              ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                              : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                            '&:hover': {
                              bgcolor: isDarkMode
                                ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                                : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                            }
                          }}
                          fullWidth
                        >
                          Add to Subscription
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                mb: 3,
                color: isDarkMode ? '#E0F2FE' : '#1E293B'
              }}
            >
              How often would you like to receive your medicines?
            </Typography>
            <Grid container spacing={3}>
              {frequencyOptions.map((option) => (
                <Grid item xs={12} sm={6} key={option.value}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      textAlign: 'center',
                      border: subscriptionData.frequency === option.value ? 2 : 1,
                      borderColor: subscriptionData.frequency === option.value 
                        ? (isDarkMode ? '#60A5FA' : '#1E40AF')
                        : (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 64, 175, 0.2)'),
                      bgcolor: subscriptionData.frequency === option.value 
                        ? (isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(30, 64, 175, 0.1)')
                        : (isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF'),
                      transition: 'all 0.3s ease',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: isDarkMode
                          ? '0 8px 24px rgba(59, 130, 246, 0.2)'
                          : '0 8px 24px rgba(30, 64, 175, 0.15)',
                        transform: 'translateY(-2px)',
                        borderColor: isDarkMode ? '#60A5FA' : '#1E40AF'
                      }
                    }}
                    onClick={() => setSubscriptionData(prev => ({ ...prev, frequency: option.value }))}
                  >
                    <Typography variant="h2" sx={{ mb: 1 }}>
                      {option.icon}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        color: isDarkMode ? '#E0F2FE' : '#1E293B',
                        fontWeight: 600
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: isDarkMode ? '#94A3B8' : '#64748B'
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Start Date */}
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={subscriptionData.startDate}
              onChange={(e) => setSubscriptionData(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ 
                mt: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                }
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            {/* Payment Method Selection */}
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
            >
              Payment Method
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                value={subscriptionData.paymentMethod}
                onChange={handlePaymentMethodSelection}
              >
                {userPaymentMethods.map((method) => (
                  <FormControlLabel
                    key={method.id}
                    value={method.id}
                    control={
                      <Radio 
                        sx={{
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          '&.Mui-checked': {
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon sx={{ color: isDarkMode ? '#60A5FA' : '#1E40AF' }} />
                        <Typography sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                          {method.label}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
                <FormControlLabel
                  value="new"
                  control={
                    <Radio 
                      sx={{
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        '&.Mui-checked': {
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                      Add New Payment Method
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* Delivery Address */}
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
            >
              Delivery Address
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={subscriptionData.deliveryAddress}
                onChange={handleAddressSelection}
              >
                {userAddresses.map((address) => (
                  <FormControlLabel
                    key={address.id}
                    value={address.id}
                    control={
                      <Radio 
                        sx={{
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          '&.Mui-checked': {
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography 
                          variant="subtitle2"
                          sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
                        >
                          {address.label}
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
                        >
                          {address.address}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
                <FormControlLabel
                  value="new"
                  control={
                    <Radio 
                      sx={{
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        '&.Mui-checked': {
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                      Add New Address
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
            >
              Review Your Subscription
            </Typography>
            
            {/* Subscription Summary */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 3,
                bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(240, 249, 255, 0.5)',
                border: isDarkMode 
                  ? '1px solid rgba(59, 130, 246, 0.2)' 
                  : '1px solid rgba(30, 64, 175, 0.1)',
                borderRadius: 2
              }}
            >
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
              >
                {subscriptionData.title || subscriptionData.name}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="subtitle2"
                      sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
                    >
                      Frequency
                    </Typography>
                    <Typography sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                      {frequencyOptions.find(f => f.value === subscriptionData.frequency)?.label}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="subtitle2"
                      sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
                    >
                      Start Date
                    </Typography>
                    <Typography sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                      {new Date(subscriptionData.startDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="subtitle2"
                      sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
                    >
                      Total per Delivery
                    </Typography>
                    <Typography 
                      variant="h6"
                      sx={{ 
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        fontWeight: 700
                      }}
                    >
                      ৳{calculateTotal().toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Divider 
                sx={{ 
                  my: 2,
                  borderColor: isDarkMode 
                    ? 'rgba(59, 130, 246, 0.2)' 
                    : 'rgba(30, 64, 175, 0.1)'
                }} 
              />
              
              {/* Items List */}
              <Typography 
                variant="subtitle2"
                gutterBottom
                sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
              >
                Items ({subscriptionData.items.length})
              </Typography>
              <List>
                {subscriptionData.items.map((item, index) => (
                  <ListItem key={item.productId} sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.productName}
                      secondary={`Quantity: ${item.quantity}`}
                      primaryTypographyProps={{
                        sx: { color: isDarkMode ? '#E0F2FE' : '#1E293B' }
                      }}
                      secondaryTypographyProps={{
                        sx: { color: isDarkMode ? '#94A3B8' : '#64748B' }
                      }}
                    />
                    <Typography 
                      variant="body2"
                      sx={{ color: isDarkMode ? '#E0F2FE' : '#1E293B' }}
                    >
                      ৳{(item.price * item.quantity).toFixed(2)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Terms and Conditions */}
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)',
                border: isDarkMode 
                  ? '1px solid rgba(59, 130, 246, 0.2)' 
                  : '1px solid rgba(30, 64, 175, 0.1)',
                '& .MuiAlert-icon': {
                  color: isDarkMode ? '#60A5FA' : '#1E40AF'
                }
              }}
            >
              <Typography 
                variant="body2"
                sx={{ color: isDarkMode ? '#BFDBFE' : '#64748B' }}
              >
                By creating this subscription, you agree to automatic billing and delivery according to the selected frequency. 
                You can pause, modify, or cancel your subscription at any time from your dashboard.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      p: 2,
      bgcolor: isDarkMode ? 'transparent' : 'transparent'
    }}>
      <Stepper 
        activeStep={activeStep} 
        sx={{ 
          mb: 4,
          '& .MuiStepLabel-root .Mui-completed': {
            color: isDarkMode ? '#60A5FA' : '#1E40AF',
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: isDarkMode ? '#60A5FA' : '#1E40AF',
          },
          '& .MuiStepLabel-label': {
            color: isDarkMode ? '#BFDBFE' : '#64748B',
            '&.Mui-active': {
              color: isDarkMode ? '#E0F2FE' : '#1E293B',
            },
            '&.Mui-completed': {
              color: isDarkMode ? '#60A5FA' : '#1E40AF',
            }
          },
          '& .MuiStepIcon-root': {
            color: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
            '&.Mui-active': {
              color: isDarkMode ? '#60A5FA' : '#1E40AF',
            },
            '&.Mui-completed': {
              color: isDarkMode ? '#60A5FA' : '#1E40AF',
            }
          }
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            bgcolor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(220, 38, 38, 0.05)',
            border: isDarkMode 
              ? '1px solid rgba(239, 68, 68, 0.2)' 
              : '1px solid rgba(220, 38, 38, 0.1)',
            '& .MuiAlert-icon': {
              color: isDarkMode ? '#F87171' : '#DC2626'
            }
          }}
        >
          <Typography sx={{ color: isDarkMode ? '#F87171' : '#DC2626' }}>
          {error}
          </Typography>
        </Alert>
      )}

      {renderStepContent()}

      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3 }}>
        <Button
          color="inherit"
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ 
            mr: 1,
            color: isDarkMode ? '#BFDBFE' : '#64748B',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
            },
            '&.Mui-disabled': {
              color: isDarkMode ? 'rgba(191, 219, 254, 0.3)' : 'rgba(100, 116, 139, 0.3)'
            }
          }}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleCreateSubscription}
            disabled={loading}
            sx={{
              bgcolor: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              '&:hover': {
                bgcolor: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              }
            }}
          >
            {loading ? 'Creating...' : 'Create Subscription'}
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleNext}
            sx={{
              bgcolor: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              '&:hover': {
                bgcolor: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              }
            }}
          >
            Next
          </Button>
        )}
      </Box>

      {/* Add New Address Dialog */}
      <Dialog 
        open={showAddressDialog} 
        onClose={handleCloseAddressDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? '#E0F2FE' : '#1E293B',
          bgcolor: isDarkMode 
            ? 'rgba(30, 64, 175, 0.2)' 
            : 'rgba(30, 64, 175, 0.05)',
          borderBottom: isDarkMode 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(30, 64, 175, 0.1)'
        }}>
          Add New Address
        </DialogTitle>
        <DialogContent sx={{ bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF' }}>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Address Label (e.g., Home, Office)"
              value={newAddress.label}
              onChange={(e) => setNewAddress(prev => ({ ...prev, label: e.target.value }))}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                }
              }}
            />
            <TextField
              fullWidth
              label="Street Address"
              value={newAddress.street}
              onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                }
              }}
              multiline
              rows={2}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '& fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: isDarkMode ? '#94A3B8' : '#64748B',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={newAddress.state}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, state: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '& fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: isDarkMode ? '#94A3B8' : '#64748B',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={newAddress.zipCode}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, zipCode: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '& fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: isDarkMode ? '#94A3B8' : '#64748B',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={newAddress.country}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '& fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: isDarkMode ? '#94A3B8' : '#64748B',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2,
          bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
          borderTop: isDarkMode 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(30, 64, 175, 0.1)'
        }}>
          <Button 
            onClick={handleCloseAddressDialog}
            sx={{ color: isDarkMode ? '#BFDBFE' : '#64748B' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddNewAddress}
            variant="contained"
            disabled={!newAddress.street || !newAddress.city || !newAddress.state || !newAddress.zipCode}
            sx={{
              bgcolor: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              '&:hover': {
                bgcolor: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              }
            }}
          >
            Add Address
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Payment Method Dialog */}
      <Dialog 
        open={showPaymentDialog} 
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
            border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? '#E0F2FE' : '#1E293B',
          bgcolor: isDarkMode 
            ? 'rgba(30, 64, 175, 0.2)' 
            : 'rgba(30, 64, 175, 0.05)',
          borderBottom: isDarkMode 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(30, 64, 175, 0.1)'
        }}>
          Add New Payment Method
        </DialogTitle>
        <DialogContent sx={{ bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF' }}>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ 
                color: isDarkMode ? '#94A3B8' : '#64748B',
                '&.Mui-focused': {
                  color: isDarkMode ? '#60A5FA' : '#1E40AF',
                }
              }}>
                Payment Type
              </InputLabel>
              <Select
                value={newPaymentMethod.type}
                onChange={(e) => setNewPaymentMethod(prev => ({ 
                  ...prev, 
                  type: e.target.value,
                  paymentMethod: e.target.value === 'card' ? 'credit_card' : 
                                  e.target.value === 'upi' ? 'upi' : 'wallet'
                }))}
                sx={{
                  bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  },
                  '& .MuiSvgIcon-root': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                }}
              >
                <MenuItem 
                  value="card"
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                    }
                  }}
                >
                  Credit/Debit Card
                </MenuItem>
                <MenuItem 
                  value="upi"
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                    }
                  }}
                >
                  UPI
                </MenuItem>
                <MenuItem 
                  value="wallet"
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                    }
                  }}
                >
                  Wallet
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Payment Method Label"
              value={newPaymentMethod.label}
              onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, label: e.target.value }))}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                  color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  '& fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: isDarkMode ? '#64748B' : '#94A3B8',
                  opacity: 1
                }
              }}
              placeholder="e.g., My Credit Card, Work UPI"
            />

            {newPaymentMethod.type === 'card' && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last 4 Digits"
                      value={newPaymentMethod.last4}
                      onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, last4: e.target.value }))}
                      inputProps={{ maxLength: 4 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                          color: isDarkMode ? '#E0F2FE' : '#1E293B',
                          '& fieldset': {
                            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#94A3B8' : '#64748B',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ 
                        color: isDarkMode ? '#94A3B8' : '#64748B',
                        '&.Mui-focused': {
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        }
                      }}>
                        Card Brand
                      </InputLabel>
                      <Select
                        value={newPaymentMethod.brand}
                        onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, brand: e.target.value }))}
                        sx={{
                          bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                          color: isDarkMode ? '#E0F2FE' : '#1E293B',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                          },
                          '& .MuiSvgIcon-root': {
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          }
                        }}
                      >
                        <MenuItem 
                          value="Visa"
                          sx={{
                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            }
                          }}
                        >
                          Visa
                        </MenuItem>
                        <MenuItem 
                          value="Mastercard"
                          sx={{
                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            }
                          }}
                        >
                          Mastercard
                        </MenuItem>
                        <MenuItem 
                          value="RuPay"
                          sx={{
                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            }
                          }}
                        >
                          RuPay
                        </MenuItem>
                        <MenuItem 
                          value="American Express"
                          sx={{
                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            }
                          }}
                        >
                          American Express
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ 
                        color: isDarkMode ? '#94A3B8' : '#64748B',
                        '&.Mui-focused': {
                          color: isDarkMode ? '#60A5FA' : '#1E40AF',
                        }
                      }}>
                        Card Type
                      </InputLabel>
                      <Select
                        value={newPaymentMethod.paymentMethod}
                        onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, paymentMethod: e.target.value }))}
                        sx={{
                          bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                          color: isDarkMode ? '#E0F2FE' : '#1E293B',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                          },
                          '& .MuiSvgIcon-root': {
                            color: isDarkMode ? '#60A5FA' : '#1E40AF',
                          }
                        }}
                      >
                        <MenuItem 
                          value="credit_card"
                          sx={{
                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            }
                          }}
                        >
                          Credit Card
                        </MenuItem>
                        <MenuItem 
                          value="debit_card"
                          sx={{
                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                            color: isDarkMode ? '#E0F2FE' : '#1E293B',
                            '&:hover': {
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                            }
                          }}
                        >
                          Debit Card
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}

            {newPaymentMethod.type === 'upi' && (
              <TextField
                fullWidth
                label="UPI ID"
                value={newPaymentMethod.upiId}
                onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, upiId: e.target.value }))}
                placeholder="user@upi"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                    '& fieldset': {
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#94A3B8' : '#64748B',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#60A5FA' : '#1E40AF',
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: isDarkMode ? '#64748B' : '#94A3B8',
                    opacity: 1
                  }
                }}
              />
            )}

            {newPaymentMethod.type === 'wallet' && (
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#60A5FA' : '#1E40AF',
                  }
                }}>
                  Wallet Provider
                </InputLabel>
                <Select
                  value={newPaymentMethod.walletProvider}
                  onChange={(e) => setNewPaymentMethod(prev => ({ ...prev, walletProvider: e.target.value }))}
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    },
                    '& .MuiSvgIcon-root': {
                      color: isDarkMode ? '#60A5FA' : '#1E40AF',
                    }
                  }}
                >
                  <MenuItem 
                    value="PayTM"
                    sx={{
                      bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                      }
                    }}
                  >
                    PayTM
                  </MenuItem>
                  <MenuItem 
                    value="PhonePe"
                    sx={{
                      bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                      }
                    }}
                  >
                    PhonePe
                  </MenuItem>
                  <MenuItem 
                    value="Google Pay"
                    sx={{
                      bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                      }
                    }}
                  >
                    Google Pay
                  </MenuItem>
                  <MenuItem 
                    value="Amazon Pay"
                    sx={{
                      bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#FFFFFF',
                      color: isDarkMode ? '#E0F2FE' : '#1E293B',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(30, 64, 175, 0.05)'
                      }
                    }}
                  >
                    Amazon Pay
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2,
          bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
          borderTop: isDarkMode 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(30, 64, 175, 0.1)'
        }}>
          <Button 
            onClick={handleClosePaymentDialog}
            sx={{ color: isDarkMode ? '#BFDBFE' : '#64748B' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddNewPaymentMethod}
            variant="contained"
            disabled={!newPaymentMethod.label || 
              (newPaymentMethod.type === 'card' && !newPaymentMethod.last4) ||
              (newPaymentMethod.type === 'upi' && !newPaymentMethod.upiId) ||
              (newPaymentMethod.type === 'wallet' && !newPaymentMethod.walletProvider)
            }
            sx={{
              bgcolor: isDarkMode
                ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
              '&:hover': {
                bgcolor: isDarkMode
                  ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              }
            }}
          >
            Add Payment Method
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateSubscription;
